/**
 * One product type's card: the legacy yes/no declaration (optionally
 * per-member, QL-505) when no assessment is configured, or a header with the
 * category verdict badge plus one `ModeRow` per enabled assessment mode.
 */

import { memberLineAttribution } from "@repo/shared";
import type { ProductTypeAssessmentMode, ProductTypeAssessmentPublic } from "@repo/shared/types";
import { ChevronDown, ChevronRight } from "lucide-react";
import { type ReactNode, useId } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { OptionRow } from "@/components/ui/option-row";
import { useT } from "@/lib/i18n/use-t";
import { type DeclarationMemberState, DeclarationSummary } from "./declaration-summary";
import { MemberLine } from "./member-line";
import {
  type AssessmentLine,
  lineAnswered,
  ModeAction,
  type ModeGroupState,
  ModeRow,
} from "./mode-row";
import { T } from "./product-type-block.i18n";

export type ProductTypeBlockRow = {
  key: string;
  mode: ProductTypeAssessmentMode;
  assessment: ProductTypeAssessmentPublic;
  single?: AssessmentLine;
  group?: ModeGroupState;
};

type ProductTypeBlockProps = {
  /** QL-654 — 2+ representatives: the member toggle says "representatives". */
  representativeList: boolean;
  typeLabel: string;
  formatDate: (iso: string) => string;
  /** Precomputed warning text ("in use but not suitable"), or null. */
  inUseWarning: string | null;
  // Declaration-only (no assessment configured for this type).
  declarationSingle?: {
    checked: boolean;
    actionable: boolean;
    onToggle: (checked: boolean) => void;
  };
  declarationGroup?: {
    memberSummary?: { passed: number; total: number };
    expanded: boolean;
    onToggleExpand: () => void;
    members: DeclarationMemberState[];
  };
  // Assessment path.
  verdictBadge?: ReactNode;
  rows?: ProductTypeBlockRow[];
};

export function ProductTypeBlock({
  representativeList,
  typeLabel,
  formatDate,
  inUseWarning,
  declarationSingle,
  declarationGroup,
  verdictBadge,
  rows,
}: ProductTypeBlockProps) {
  const t = useT(T);
  const checkboxId = useId();
  // QL-654 — 2+ representatives answer in the same list as a group's members.
  const expandLabel = representativeList ? t.expandRepresentatives : t.expandMembers;
  const collapseLabel = representativeList ? t.collapseRepresentatives : t.collapseMembers;
  // Declaration-only, single answering subject — the legacy checkbox row.
  if (declarationSingle) {
    return (
      <div className="p-3 border rounded-lg space-y-2">
        <OptionRow>
          <Checkbox
            id={checkboxId}
            checked={declarationSingle.checked}
            onCheckedChange={(checked) => declarationSingle.onToggle(checked === true)}
            disabled={!declarationSingle.actionable}
          />
          <Label htmlFor={checkboxId} className="font-normal">
            {typeLabel}
          </Label>
        </OptionRow>
        {inUseWarning && <p className="text-sm text-destructive">{inUseWarning}</p>}
      </div>
    );
  }

  // Declaration-only, group without a representative — group summary + members.
  if (declarationGroup) {
    return (
      <div className="p-3 border rounded-lg space-y-2">
        <DeclarationSummary
          representativeList={representativeList}
          typeLabel={typeLabel}
          {...declarationGroup}
        />
        {inUseWarning && <p className="text-sm text-destructive">{inUseWarning}</p>}
      </div>
    );
  }

  // Assessment path, exactly one enabled mode: fold the mode label and its
  // action/state into the product-type header — one status tag (the type
  // verdict, which equals the single mode's own verdict), no separate
  // ModeRow. In members mode the header itself is the expand toggle and the
  // member lines sit directly under it. Two or more modes fall through to
  // the header + ModeRow list below, unchanged.
  if (rows && rows.length === 1) {
    const row = rows[0];
    const modeLabel = row.mode === "QUIZ" ? t.quizLabel : t.questionnaireLabel;

    if (row.single) {
      return (
        <div className="p-3 border rounded-lg space-y-2">
          <div className="flex min-h-9 flex-wrap items-center justify-between gap-x-3 gap-y-2">
            <div className="flex flex-wrap items-baseline gap-x-2">
              <span className="font-medium">{typeLabel}</span>
              <span className="text-sm text-muted-foreground">{modeLabel}</span>
            </div>
            <div className="ml-auto flex flex-wrap items-center justify-end gap-x-3 gap-y-2">
              <ModeAction
                mode={row.mode}
                assessment={row.assessment}
                line={row.single}
                formatDate={formatDate}
              />
              {verdictBadge}
            </div>
          </div>
          {inUseWarning && <p className="text-sm text-destructive">{inUseWarning}</p>}
        </div>
      );
    }

    if (row.group) {
      const group = row.group;
      const countText = group.memberSummary
        ? t.memberCountPassed(group.memberSummary.passed, group.memberSummary.total)
        : null;
      return (
        <div className="p-3 border rounded-lg space-y-2">
          <button
            type="button"
            onClick={group.onToggleExpand}
            aria-expanded={group.expanded}
            aria-label={`${group.expanded ? collapseLabel : expandLabel}: ${typeLabel}`}
            className="flex min-h-9 w-full items-center justify-between gap-3 text-left"
          >
            <span className="flex min-w-0 items-center gap-2">
              {group.expanded ? (
                <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
              )}
              <span className="truncate font-medium">{typeLabel}</span>
              <span className="shrink-0 text-sm text-muted-foreground">{modeLabel}</span>
            </span>
            <span className="flex shrink-0 items-center gap-2 whitespace-nowrap">
              {countText && <span className="text-sm text-muted-foreground">{countText}</span>}
              {verdictBadge}
            </span>
          </button>
          {group.expanded && (
            <div className="space-y-1 pl-6">
              {group.members.map((member) => (
                <MemberLine
                  key={member.subjectId}
                  subjectId={member.subjectId}
                  name={member.name}
                  {...memberLineAttribution({
                    subjectId: member.subjectId,
                    liveRepresentative: member.memberRepresentative,
                    answered: lineAnswered(member, row.mode),
                    answeredBy: member.answeredBy,
                    advisorOrExternal: !!member.external,
                  })}
                  language={t.locale}
                >
                  <ModeAction
                    mode={row.mode}
                    assessment={row.assessment}
                    line={member}
                    formatDate={formatDate}
                  />
                </MemberLine>
              ))}
            </div>
          )}
          {inUseWarning && <p className="text-sm text-destructive">{inUseWarning}</p>}
        </div>
      );
    }
  }

  // Assessment path, two or more modes: header with the overall verdict, one sub-row per mode.
  return (
    <div className="p-3 border rounded-lg space-y-3">
      <div className="flex min-h-9 items-center justify-between gap-3">
        <div className="min-w-0 truncate font-medium">{typeLabel}</div>
        {verdictBadge}
      </div>
      {/* Keyed by assessment id, not mode — a duplicate-mode data anomaly must
          not collide React keys. */}
      <div className="space-y-2">
        {rows?.map((row) => (
          <div key={row.key}>
            <ModeRow
              mode={row.mode}
              assessment={row.assessment}
              representativeList={representativeList}
              formatDate={formatDate}
              single={row.single}
              group={row.group}
            />
          </div>
        ))}
      </div>
      {inUseWarning && <p className="text-sm text-destructive">{inUseWarning}</p>}
    </div>
  );
}
